import React from 'react';
import axios from "axios";
import { useCallback, useEffect, useLayoutEffect, useState, useRef } from "react";
import { FaCirclePlus } from "react-icons/fa6";
import { MdKeyboardArrowDown } from "react-icons/md";
import { MdKeyboardArrowUp } from "react-icons/md";
import { getAuthHeaders } from "../../utils/auth";
import {
  MAX_ATTACHMENTS,
  MAX_FILE_BYTES,
  ATTACHMENT_ACCEPT,
  validateAttachmentFiles,
  formatAttachmentFileSize,
  countHrRequestAttachments,
} from '../../utils/attachmentUpload';
import {
  formatUsDateFromIso,
  formatUsDateFromDateTime,
  formatUsDateTimeFromIso,
  todayIsoLocal,
  calculateBusinessDaysYmd,
} from '../../utils/usDateFormat';
import { formatEmployeeNameWithNo } from '../../utils/employeeDisplay';
import UsDatePicker from '../../components/UsDatePicker';
import Loader from '../../components/Loader/Loader';
import {
  fetchHrRequestById,
  isHrNotificationType,
  parseNotificationActionError,
  resolveHrRequestView,
} from '../../utils/notificationNavigation';
import ReusablePopup from '../../components/Modals/ReuseableModal';
import DetailedLeavePopup from '../../components/Modals/LeavePopUp';
import ColumnCategoryFilterHeader from '../../components/ColumnCategoryFilterHeader/ColumnCategoryFilterHeader';
import {
  selectedLeaveTypeRequiresAttachments,
  selectedLeaveTypeAllowsPastDates,
  collectHrRequestFormErrors,
  formatHrRequestValidationMessage,
  isLeaveRequestCategory,
  isSalaryCertificateCategory,
  isTimeOffRequestCategory,
  isReasonFieldVisible,
  isReasonOnlyAdminCategory,
  resolveHrRequestReason,
} from '../../utils/leaveTypeAttachments';
import { canDeleteHrRequest } from '../../utils/hrRequestApproval';

const HR_STATUS_FILTER_OPTIONS = [
  { label: 'Request Submitted', value: 'Request Submitted' },
  { label: 'Manager Approved', value: 'Manager Approved' },
  { label: 'Payroll Manager Approved', value: 'Payroll Manager Approved' },
  { label: 'HR Manager Approved', value: 'HR Manager Approved' },
  { label: 'Manager Rejected', value: 'Manager Rejected' },
  { label: 'Payroll Manager Rejected', value: 'Payroll Manager Rejected' },
  { label: 'HR Manager Rejected', value: 'HR Manager Rejected' },
];

const HR_REQUESTS_PAGE_SIZE_KEY = 'hr-requests-page-size';
const HR_REQUESTS_PAGE_SIZE_OPTIONS = [10, 30, 50, 80, 100];
const DEFAULT_HR_REQUESTS_PAGE_SIZE = 10;

const readStoredHrRequestsPageSize = () => {
  try {
    const raw = localStorage.getItem(HR_REQUESTS_PAGE_SIZE_KEY);
    const parsed = Number(raw);
    if (HR_REQUESTS_PAGE_SIZE_OPTIONS.includes(parsed)) {
      return parsed;
    }
  } catch (_) {
    // Ignore storage errors and fall back to default.
  }
  return DEFAULT_HR_REQUESTS_PAGE_SIZE;
};

const INITIAL_NEW_LEAVE = {
  request_type: '',
  reason: '',
  start_date: '',
  end_date: '',
  leave_type_id: '',
  leave_type_name: '',
  certificate_type: '',
  purpose: '',
  request_category_id: '',
  request_category_name: '',
  tracking_id: '',
  ph_status: false,
  advance_salary: false,
  payslip_from_date: '',
  payslip_to_date: '',
  reallocation_reason: '',
  transfer_from_location: '',
  transfer_to_location: '',
  effective_from_date: '',
  section: '',
  resume_status: '',
  actual_resume_date: '',
  resume_remarks: '',
  leave_request_reference: '',
};

const INITIAL_TIME_OFF = {
  time_from_date: '',
  time_from_hour: '',
  time_to_date: '',
  time_to_hour: '',
};

const formatTimeInputValue = (date) => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

const MAX_TIME_OFF_HOURS_PER_REQUEST = 6;

const getTimeOffMaxEndTime = (fromDate, fromTime, maxHours = MAX_TIME_OFF_HOURS_PER_REQUEST) => {
  if (!fromDate || !fromTime) return '';

  const hoursCap = Number(maxHours);
  if (!Number.isFinite(hoursCap) || hoursCap <= 0) return fromTime;

  const start = new Date(`${fromDate}T${fromTime}`);
  if (Number.isNaN(start.getTime())) return '';

  const maxEnd = new Date(start.getTime() + hoursCap * 60 * 60 * 1000);
  const endDateLocal = [
    maxEnd.getFullYear(),
    String(maxEnd.getMonth() + 1).padStart(2, '0'),
    String(maxEnd.getDate()).padStart(2, '0'),
  ].join('-');
  const sameDay = endDateLocal === fromDate;

  return sameDay ? formatTimeInputValue(maxEnd) : '23:59';
};

const computeTimeOffDurationHours = (fromDate, fromTime, toDate, toTime) => {
  if (!fromDate || !fromTime || !toDate || !toTime) return 0;
  const fromDateTime = new Date(`${fromDate}T${fromTime}`);
  const toDateTime = new Date(`${toDate}T${toTime}`);
  if (Number.isNaN(fromDateTime.getTime()) || Number.isNaN(toDateTime.getTime())) return 0;
  const diffMs = toDateTime.getTime() - fromDateTime.getTime();
  if (diffMs <= 0) return 0;
  return Math.round((diffMs / (60 * 60 * 1000)) * 100) / 100;
};

const formatHoursLabel = (hours) => {
  const value = Number(hours);
  if (!Number.isFinite(value)) return '0';
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
};

const hasAdminPrivileges = (role) => {
  return role === 'admin' || role === 'super';
};

const isAppliedDateRangeInvalid = (fromDate, toDate) =>
  Boolean(fromDate && toDate && toDate < fromDate);

const RequestAttachmentBadge = ({ leave }) => {
  const count = countHrRequestAttachments(leave);
  if (count === 0) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-xs font-semibold rounded-full"
      title={`${count} attachment${count === 1 ? '' : 's'}`}
      aria-label={`${count} attachment${count === 1 ? '' : 's'}`}
    >
      <svg
        className="w-3.5 h-3.5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.414a4 4 0 10-5.656-5.656L4.343 11.172a6 6 0 108.485 8.485L20 12.5"
        />
      </svg>
      {count}
    </span>
  );
};

// Request management component with enhanced workflow
const RequestManagement = ({
  userProfile,
  currentView,
  pendingNotificationAction,
  onNotificationActionHandled,
  onNavigateView,
}) => {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [leaveTypeFilter, setLeaveTypeFilter] = useState([]);
  const [openHeaderFilter, setOpenHeaderFilter] = useState(null);
  const [dateError, setDateError] = useState('');
  const [leaveBalance, setLeaveBalance] = useState(0);
  const [expandedMobileCard, setExpandedMobileCard] = useState(null);
  const [requestIdFilter, setRequestIdFilter] = useState('');
  const [employeeNoFilter, setEmployeeNoFilter] = useState('');
  const [debouncedRequestId, setDebouncedRequestId] = useState('');
  const [debouncedEmployeeNo, setDebouncedEmployeeNo] = useState('');
  const [appliedFromDraft, setAppliedFromDraft] = useState('');
  const [appliedToDraft, setAppliedToDraft] = useState('');
  const [appliedFromQuery, setAppliedFromQuery] = useState('');
  const [appliedToQuery, setAppliedToQuery] = useState('');
  const [appliedDateError, setAppliedDateError] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [fetchError, setFetchError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [limit, setLimit] = useState(readStoredHrRequestsPageSize);
  const [skip, setSkip] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const fetchAbortRef = useRef(null);
  const fetchSeqRef = useRef(0);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [timeOffError, setTimeOffError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [popupMessage, setPopupMessage] = useState({ success: null, failure: null, info: null });
  const [formSubmitError, setFormSubmitError] = useState('');
  const [leaveRequestReferences, setLeaveRequestReferences] = useState([]);
  const [locations, setLocations] = useState([]);
  const [dateTimeforTimeOff, setDateTimeforTimeOff] = useState({ ...INITIAL_TIME_OFF });
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [timeOffMonthlyUsage, setTimeOffMonthlyUsage] = useState(null);
  const leaveStartDateRef = useRef(null);
  const leaveEndDateRef = useRef(null);
  const timeOffRemainingHours =
    timeOffMonthlyUsage?.remaining_hours != null
      ? Number(timeOffMonthlyUsage.remaining_hours)
      : MAX_TIME_OFF_HOURS_PER_REQUEST;
  const timeOffMaxHours = Math.min(
    MAX_TIME_OFF_HOURS_PER_REQUEST,
    Number.isFinite(timeOffRemainingHours) ? timeOffRemainingHours : MAX_TIME_OFF_HOURS_PER_REQUEST
  );
  const maxTimeOffEndTime = getTimeOffMaxEndTime(
    dateTimeforTimeOff.time_from_date,
    dateTimeforTimeOff.time_from_hour,
    timeOffMaxHours
  );

  const handleTimeOffChange = (e) => {
    const { name, value } = e.target;
    setDateTimeforTimeOff((prev) => {
      const next = {
        ...prev,
        [name]: value,
      };

      if (name === 'time_from_date') {
        next.time_to_date = value;
      }

      if (name === 'time_from_hour' && next.time_from_date) {
        next.time_to_date = next.time_from_date;
      }

      const remainingCap =
        timeOffMonthlyUsage?.remaining_hours != null
          ? Number(timeOffMonthlyUsage.remaining_hours)
          : MAX_TIME_OFF_HOURS_PER_REQUEST;
      const hoursCap = Math.min(
        MAX_TIME_OFF_HOURS_PER_REQUEST,
        Number.isFinite(remainingCap) ? remainingCap : MAX_TIME_OFF_HOURS_PER_REQUEST
      );
      const maxEndTime = getTimeOffMaxEndTime(
        next.time_from_date,
        next.time_from_hour,
        hoursCap
      );
      if (name === 'time_to_hour' && maxEndTime && value > maxEndTime) {
        next.time_to_hour = maxEndTime;
      } else if (
        next.time_to_hour &&
        next.time_from_date &&
        next.time_from_hour &&
        maxEndTime &&
        next.time_to_hour > maxEndTime
      ) {
        next.time_to_hour = maxEndTime;
      }

      return next;
    });
  };

  useLayoutEffect(() => {
    setSkip(0);
    setStatusFilter([]);
    setCategoryFilter([]);
    setLeaveTypeFilter([]);
    setOpenHeaderFilter(null);
    setAppliedFromDraft('');
    setAppliedToDraft('');
    setAppliedFromQuery('');
    setAppliedToQuery('');
    setAppliedDateError('');
    setSortOrder('desc');
    setFetchError(null);
  }, [currentView]);

  const handleHeaderFilterOpenChange = useCallback((filterKey, isOpen) => {
    setOpenHeaderFilter((prev) => {
      if (isOpen) return filterKey;
      return prev === filterKey ? null : prev;
    });
  }, []);

  const handlePageSizeChange = (event) => {
    const nextLimit = Number(event.target.value);
    if (!HR_REQUESTS_PAGE_SIZE_OPTIONS.includes(nextLimit)) {
      return;
    }
    setLimit(nextLimit);
    setSkip(0);
    try {
      localStorage.setItem(HR_REQUESTS_PAGE_SIZE_KEY, String(nextLimit));
    } catch (_) {
      // Ignore storage errors; selection still applies for this session.
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedRequestId(requestIdFilter.trim());
      setDebouncedEmployeeNo(employeeNoFilter.trim());
      setSkip(0);
    }, 500);
    return () => clearTimeout(handler);
  }, [requestIdFilter, employeeNoFilter]);

  useEffect(() => {
    setSkip(0);
  }, [statusFilter, categoryFilter, leaveTypeFilter, sortOrder]);

  const appendColumnFilters = useCallback((params) => {
    if (statusFilter.length > 0) {
      params.append('status_filter', statusFilter.join(','));
    }
    if (categoryFilter.length > 0) {
      params.append('category_names', categoryFilter.join(','));
    }
    if (leaveTypeFilter.length > 0) {
      params.append('leave_types', leaveTypeFilter.join(','));
    }
  }, [statusFilter, categoryFilter, leaveTypeFilter]);

  const appendHrListScopeAndFilters = useCallback((params) => {
    if (currentView === 'my_team_requests') {
      params.append('request_scope', 'team');
    } else if (currentView === 'my_leave_requests') {
      params.append('request_scope', 'own');
      params.append('category_filter', 'leave_requests');
    } else if (currentView === 'my_admin_requests') {
      params.append('request_scope', 'own');
      params.append('category_filter', 'admin_requests');
    } else {
      const isAdminSuper =
        userProfile?.role === 'admin' || userProfile?.role === 'super';
      if (isAdminSuper) {
        params.append('request_scope', 'all');
      } else {
        params.append('request_scope', 'own');
      }
      if (currentView === 'requests') {
        params.append('category_filter', 'leave_requests');
      } else if (currentView === 'admin_requests') {
        params.append('category_filter', 'admin_requests');
      }
    }
    if (debouncedRequestId) {
      params.append('request_id', debouncedRequestId);
    }
    if (debouncedEmployeeNo) {
      params.append('employee_no', debouncedEmployeeNo);
    }
    const appliedRangeInvalid = isAppliedDateRangeInvalid(appliedFromQuery, appliedToQuery);
    if (!appliedRangeInvalid) {
      if (appliedFromQuery) {
        params.append('applied_from', appliedFromQuery);
      }
      if (appliedToQuery) {
        params.append('applied_to', appliedToQuery);
      }
    }
    appendColumnFilters(params);
  }, [
    appendColumnFilters,
    appliedFromQuery,
    appliedToQuery,
    currentView,
    debouncedEmployeeNo,
    debouncedRequestId,
    userProfile?.role,
  ]);

  const fetchHRequests = useCallback(async () => {
    const requestSeq = ++fetchSeqRef.current;

    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    try {
      setLoading(true);
      setFetchError(null);
      const headers = await getAuthHeaders();
      const params = new URLSearchParams({
        skip: skip.toString(),
        limit: limit.toString(),
        sort_order: sortOrder,
      });

      if (currentView === 'deleted_requests') {
        if (debouncedRequestId) {
          params.append('request_id', debouncedRequestId);
        }
        if (debouncedEmployeeNo) {
          params.append('employee_no', debouncedEmployeeNo);
        }
        appendColumnFilters(params);
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/hr-requests/deleted?${params.toString()}`,
          { headers, signal: controller.signal }
        );
        if (requestSeq !== fetchSeqRef.current) return;

        const deletedRequests = response.data.deleted_hr_requests || [];
        setLeaves(deletedRequests);
        setTotalCount(response?.data?.total || 0);
        return;
      }

      appendHrListScopeAndFilters(params);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/hr-requests?${params.toString()}`,
        { headers, signal: controller.signal }
      );
      if (requestSeq !== fetchSeqRef.current) return;

      setLeaves(response.data.hr_requests || []);
      setLeaveBalance(response?.data?.leave_balance ?? 0);
      setTotalCount(response?.data?.total || 0);
    } catch (error) {
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
        return;
      }
      if (requestSeq !== fetchSeqRef.current) return;
      console.error('Error fetching hr requests:', error);
      setFetchError('Failed to load requests. Please try again.');
    } finally {
      if (requestSeq === fetchSeqRef.current) {
        setLoading(false);
      }
    }
  }, [
    appendColumnFilters,
    appendHrListScopeAndFilters,
    currentView,
    debouncedEmployeeNo,
    debouncedRequestId,
    limit,
    skip,
    sortOrder,
  ]);

  useEffect(() => {
    fetchHRequests();
    return () => {
      if (fetchAbortRef.current) {
        fetchAbortRef.current.abort();
      }
    };
  }, [fetchHRequests]);

  const handleApplyDateFilter = () => {
    if (isAppliedDateRangeInvalid(appliedFromDraft, appliedToDraft)) {
      setAppliedDateError('End date must be on or after the start date.');
      return;
    }
    setAppliedDateError('');
    setAppliedFromQuery(appliedFromDraft);
    setAppliedToQuery(appliedToDraft);
    setSkip(0);
  };

  const handleClearDateFilter = () => {
    setAppliedFromDraft('');
    setAppliedToDraft('');
    setAppliedFromQuery('');
    setAppliedToQuery('');
    setAppliedDateError('');
    setSkip(0);
  };

  const handleSubmittedSort = (nextSortOrder) => {
    if (nextSortOrder === sortOrder) return;
    setSortOrder(nextSortOrder);
    setSkip(0);
  };

  const appliedDraftRangeInvalid = isAppliedDateRangeInvalid(appliedFromDraft, appliedToDraft);
  const canApplyDateFilter = !appliedDateError && !appliedDraftRangeInvalid;

  useEffect(() => {
    if (
      !pendingNotificationAction
      || !isHrNotificationType(pendingNotificationAction.type)
    ) {
      return undefined;
    }

    let cancelled = false;

    const openFromNotification = async () => {
      try {
        const headers = await getAuthHeaders();
        const hrRequest = await fetchHrRequestById(
          pendingNotificationAction.entityId,
          headers
        );
        if (cancelled) return;

        const targetView = resolveHrRequestView(
          userProfile,
          hrRequest,
          pendingNotificationAction.type
        );
        if (targetView && targetView !== currentView && onNavigateView) {
          onNavigateView(targetView);
        }
        setSelectedLeave(hrRequest);
      } catch (error) {
        if (!cancelled) {
          setPopupMessage({
            success: null,
            failure: null,
            info: parseNotificationActionError(error),
          });
        }
      } finally {
        if (!cancelled && onNotificationActionHandled) {
          onNotificationActionHandled();
        }
      }
    };

    openFromNotification();

    return () => {
      cancelled = true;
    };
  }, [pendingNotificationAction, onNotificationActionHandled, onNavigateView, userProfile]);

  const [newLeave, setNewLeave] = useState({ ...INITIAL_NEW_LEAVE });

  const [selectedCategory, setSelectedCategory] = useState('');
  const [hrRequestFiles, setHrRequestFiles] = useState([]);

  const onlyReason = isReasonOnlyAdminCategory(selectedCategory);

  const attachmentsRequiredForLeave =
    isLeaveRequestCategory(selectedCategory) &&
    selectedLeaveTypeRequiresAttachments(leaveTypes, newLeave.leave_type_id);

  const allowPastLeaveDates =
    isLeaveRequestCategory(selectedCategory) &&
    selectedLeaveTypeAllowsPastDates(leaveTypes, newLeave.leave_type_id);

  const showValidationFailure = useCallback((message) => {
    setFormSubmitError(message);
    setPopupMessage({ success: null, failure: message, info: null });
  }, []);

  const clearValidationFailure = useCallback(() => {
    setFormSubmitError('');
  }, []);

  const resetRequestForm = useCallback(() => {
    setNewLeave({ ...INITIAL_NEW_LEAVE });
    setSelectedCategory('');
    setHrRequestFiles([]);
    setDateTimeforTimeOff({ ...INITIAL_TIME_OFF });
    setTimeOffMonthlyUsage(null);
    setDateError('');
    setTimeOffError('');
    setFormSubmitError('');
  }, []);

  const closeRequestForm = useCallback(() => {
    resetRequestForm();
    setShowRequestForm(false);
  }, [resetRequestForm]);

  useEffect(() => {
    if (!showRequestForm) {
      resetRequestForm();
    }
  }, [showRequestForm, resetRequestForm]);

  const appendPayloadToFormData = (formData, obj) => {
    Object.entries(obj).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (typeof value === 'boolean') {
        formData.append(key, value ? 'true' : 'false');
      } else {
        formData.append(key, String(value));
      }
    });
  };

  const handleAddHrRequestFiles = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = '';
    if (picked.length === 0) return;
    const currentCount = hrRequestFiles.length;
    if (currentCount + picked.length > MAX_ATTACHMENTS) {
      setPopupMessage({
        success: null,
        failure: `You can attach at most ${MAX_ATTACHMENTS} files per request.`,
        info: null,
      });
      return;
    }
    const err = validateAttachmentFiles(picked);
    if (err) {
      setPopupMessage({ success: null, failure: err, info: null });
      return;
    }
    setHrRequestFiles((prev) => [...prev, ...picked]);
    clearValidationFailure();
  };

  const handleRemoveHrRequestFile = (index) => {
    setHrRequestFiles((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchCategory();
      fetchLocations();
      fetchLeaves();
      fetchLeaveRequestReferences();
    }, 300);
    return () => clearTimeout(handler);
  }, [currentView]);

  useEffect(() => {
    if (newLeave.start_date && newLeave.end_date) {
      if (newLeave.end_date < newLeave.start_date) {
        setDateError('End date cannot be earlier than the start date.');
        setNewLeave((prev) => ({ ...prev, days_requested: '' }));
        return;
      }

      const diffDays = calculateBusinessDaysYmd(newLeave.start_date, newLeave.end_date);

      setNewLeave((prev) => ({
        ...prev,
        days_requested: diffDays >= 0 ? diffDays : '',
      }));
      setDateError('');
    } else {
      setNewLeave((prev) => ({
        ...prev,
        days_requested: '',
      }));
      setDateError('');
    }
  }, [newLeave.start_date, newLeave.end_date]);

  const parseExportError = async (err) => {
    const data = err.response?.data;
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        const parsed = JSON.parse(text);
        if (typeof parsed.detail === 'string') return parsed.detail;
      } catch (_) {
        /* ignore */
      }
    }
    if (typeof data?.detail === 'string') return data.detail;
    return 'Failed to export HR requests. Please try again.';
  };

  const handleExport = async () => {
    if (currentView === 'deleted_requests') return;
    if (isAppliedDateRangeInvalid(appliedFromQuery, appliedToQuery)) return;

    try {
      setExporting(true);
      setExportError(null);
      const headers = await getAuthHeaders();
      const params = new URLSearchParams({ sort_order: sortOrder });
      appendHrListScopeAndFilters(params);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/hr-requests/export?${params.toString()}`,
        {
          headers,
          responseType: 'blob',
        }
      );

      const blob = new Blob([response.data], {
        type:
          response.headers['content-type'] ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";\n]+)"?/i);
      link.download = match ? match[1].trim() : 'hr_requests_export.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting HR requests:', err);
      setExportError(await parseExportError(err));
    } finally {
      setExporting(false);
    }
  };

  const fetchLeaves = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/system/leave-types`, {
        headers
      });
      setLeaveTypes(response.data.leave_types || []);
    } catch (error) {
      console.error('Error fetching leaves:', error);
    }
  };

  const fetchCategory = async () => {
    try {
      const headers = await getAuthHeaders();
      let url;
      if (currentView === 'requests' || currentView === 'my_leave_requests') {
        url = '/api/system/leave-request-categories';
      } else if (currentView === 'admin_requests' || currentView === 'my_admin_requests') {
        url = '/api/system/admin-request-categories';
      } else {
        url = '/api/system/hr-request-options';
      }
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}${url}`, {
        headers
      });
      if (url === '/api/system/hr-request-options') {
        setCategories(response.data.request_categories || []);
      } else {
        setCategories(response.data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSubmitLeave = async (e) => {
    e?.preventDefault?.();
    clearValidationFailure();

    let leaveStart = newLeave.start_date;
    let leaveEnd = newLeave.end_date;
    if (isLeaveRequestCategory(selectedCategory)) {
      leaveStart = leaveStartDateRef.current?.commit?.() ?? leaveStart;
      leaveEnd = leaveEndDateRef.current?.commit?.() ?? leaveEnd;
    }

    if (attachmentsRequiredForLeave && hrRequestFiles.length === 0) {
      showValidationFailure(
        'Please complete the required field: Attachments. At least one file is required for Sick Leave, Maternity, and Bereavement Leave.'
      );
      return;
    }

    const validationErrors = collectHrRequestFormErrors({
      selectedCategory,
      onlyReason,
      newLeave: { ...newLeave, start_date: leaveStart, end_date: leaveEnd },
      leaveTypes,
      hrRequestFiles,
      dateTimeforTimeOff,
    });
    if (validationErrors.length > 0) {
      showValidationFailure(formatHrRequestValidationMessage(validationErrors));
      return;
    }

    if (timeOffError) {
      showValidationFailure('Please fix the date/time errors before submitting.');
      return;
    }

    if (isTimeOffRequestCategory(selectedCategory)) {
      const remaining =
        timeOffMonthlyUsage?.remaining_hours != null
          ? Number(timeOffMonthlyUsage.remaining_hours)
          : null;
      const thisRequestHours = computeTimeOffDurationHours(
        dateTimeforTimeOff.time_from_date,
        dateTimeforTimeOff.time_from_hour,
        dateTimeforTimeOff.time_to_date,
        dateTimeforTimeOff.time_to_hour
      );
      if (remaining != null && Number.isFinite(remaining) && remaining <= 0) {
        showValidationFailure(
          'Monthly time-off allowance of 6 hours has been utilized for this month.'
        );
        return;
      }
      if (
        remaining != null &&
        Number.isFinite(remaining) &&
        thisRequestHours > remaining + 0.001
      ) {
        showValidationFailure(
          `This request (${formatHoursLabel(thisRequestHours)} hours) exceeds your remaining monthly time-off allowance (${formatHoursLabel(remaining)} hours).`
        );
        return;
      }
    }

    if (isLeaveRequestCategory(selectedCategory)) {
      if (!leaveStart || !leaveEnd) {
        showValidationFailure('Please enter valid start and end dates as MM/DD/YYYY.');
        return;
      }
      if (leaveEnd < leaveStart) {
        showValidationFailure('End date cannot be earlier than the start date.');
        return;
      }
    } else if (dateError) {
      showValidationFailure('Please fix the date errors before submitting.');
      return;
    }
    try {
      setIsSubmitting(true);
      const headers = await getAuthHeaders();
      const selectedLeaveTypeObj = leaveTypes.find(type => type.id === newLeave.leave_type_id);
      const reasonForSubmit = resolveHrRequestReason(selectedCategory, onlyReason, newLeave.reason);

      let payload;
      if (isTimeOffRequestCategory(selectedCategory)) {
        payload = {
          request_category_name: selectedCategory,
          time_from_date: dateTimeforTimeOff.time_from_date,
          time_from_hour: dateTimeforTimeOff.time_from_hour,
          time_to_date: dateTimeforTimeOff.time_to_date,
          time_to_hour: dateTimeforTimeOff.time_to_hour,
          reason: reasonForSubmit,
        };
      } else if (onlyReason) {
        payload = {
          reason: reasonForSubmit,
          request_category_name: selectedCategory
        };
      } else if (selectedCategory === 'Monthly Pay Slip') {
        payload = {
          request_category_name: selectedCategory,
          payslip_from_date: newLeave.payslip_from_date ? newLeave.payslip_from_date : '',
          payslip_to_date: newLeave.payslip_to_date ? newLeave.payslip_to_date : '',
          reason: reasonForSubmit,
        };
      } else if (selectedCategory === 'Location Transfer Request') {
        payload = {
          request_category_name: selectedCategory,
          reason: reasonForSubmit,
          reallocation_reason: newLeave.reallocation_reason,
          transfer_from_location: newLeave.transfer_from_location,
          transfer_to_location: newLeave.transfer_to_location,
          effective_from_date: newLeave.effective_from_date,
          section: newLeave.section
        };
      } else if (selectedCategory === 'Leave Resume') {
        payload = {
          request_category_name: selectedCategory,
          reason: reasonForSubmit,
          resume_status: newLeave.resume_status,
          actual_resume_date: newLeave.actual_resume_date,
          resume_remarks: newLeave.resume_remarks,
          leave_request_reference: newLeave.leave_request_reference
        };
      } else if (isSalaryCertificateCategory(selectedCategory)) {
        payload = {
          request_category_name: selectedCategory,
          reason: reasonForSubmit,
        };
      } else {
        payload = {
          reason: reasonForSubmit,
          start_date: leaveStart || '',
          end_date: leaveEnd || '',
          leave_type_id: newLeave.leave_type_id || '',
          leave_type_name: selectedLeaveTypeObj?.value || '',
          request_category_name: selectedCategory,
          ph_status: newLeave.ph_status,
          advance_salary: newLeave.advance_salary
        };
      }

      const formData = new FormData();
      appendPayloadToFormData(formData, payload);
      hrRequestFiles.forEach((file) => {
        formData.append('files', file, file.name);
      });

      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/hr-requests`, formData, {
        headers
      });

      closeRequestForm();
      fetchHRequests();
      setPopupMessage({
        success: 'Request submitted successfully! It will be reviewed by your reporting manager.',
        failure: null,
        info: null
      });
    } catch (error) {
      console.error('Error submitting request:', error);
      const detail = error.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg || JSON.stringify(d)).join(' ')
            : 'Error submitting request. Please try again.';
      setPopupMessage({
        success: null,
        failure: msg,
        info: null
      });
    } finally {
      setIsSubmitting(false)
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/system/locations`, {
        headers: {
          'accept': 'application/json'
        }
      });
      setLocations(response.data.locations.map(location => location.name) || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchLeaveRequestReferences = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/system/employee-leave-requests`, {
        headers
      });
      setLeaveRequestReferences(response.data.leave_requests || []);
    } catch (error) {
      console.error('Error fetching leave request references:', error);
    }
  };

  useEffect(() => {
    if (!isTimeOffRequestCategory(selectedCategory) || !showRequestForm) {
      setTimeOffMonthlyUsage(null);
      return undefined;
    }

    let cancelled = false;
    const refDate = dateTimeforTimeOff.time_from_date || todayIsoLocal();

    const fetchUsage = async () => {
      try {
        const headers = await getAuthHeaders();
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/hr-requests/time-off-monthly-usage`,
          {
            headers,
            params: { date: refDate },
          }
        );
        if (!cancelled) {
          setTimeOffMonthlyUsage(response.data || null);
        }
      } catch (error) {
        console.error('Error fetching time-off monthly usage:', error);
        if (!cancelled) {
          setTimeOffMonthlyUsage(null);
        }
      }
    };

    fetchUsage();
    return () => {
      cancelled = true;
    };
  }, [selectedCategory, dateTimeforTimeOff.time_from_date, showRequestForm]);

  useEffect(() => {
    if (!isTimeOffRequestCategory(selectedCategory) || !timeOffMonthlyUsage) {
      return;
    }
    const remaining = Number(timeOffMonthlyUsage.remaining_hours);
    if (!Number.isFinite(remaining)) return;
    const hoursCap = Math.min(MAX_TIME_OFF_HOURS_PER_REQUEST, remaining);

    setDateTimeforTimeOff((prev) => {
      if (!prev.time_from_date || !prev.time_from_hour || !prev.time_to_hour) {
        return prev;
      }
      const maxEndTime = getTimeOffMaxEndTime(
        prev.time_from_date,
        prev.time_from_hour,
        hoursCap
      );
      if (!maxEndTime || prev.time_to_hour <= maxEndTime) {
        return prev;
      }
      return { ...prev, time_to_hour: maxEndTime };
    });
  }, [timeOffMonthlyUsage, selectedCategory]);

  useEffect(() => {
    if (isTimeOffRequestCategory(selectedCategory) && 
        dateTimeforTimeOff.time_from_date && 
        dateTimeforTimeOff.time_to_date &&
        dateTimeforTimeOff.time_from_hour &&
        dateTimeforTimeOff.time_to_hour) {
      
      const fromDateTime = new Date(`${dateTimeforTimeOff.time_from_date}T${dateTimeforTimeOff.time_from_hour}`);
      const toDateTime = new Date(`${dateTimeforTimeOff.time_to_date}T${dateTimeforTimeOff.time_to_hour}`);

      const diffMs = toDateTime.getTime() - fromDateTime.getTime();
      const remainingCap =
        timeOffMonthlyUsage?.remaining_hours != null
          ? Number(timeOffMonthlyUsage.remaining_hours)
          : MAX_TIME_OFF_HOURS_PER_REQUEST;
      const maxHours = Math.min(
        MAX_TIME_OFF_HOURS_PER_REQUEST,
        Number.isFinite(remainingCap) ? remainingCap : MAX_TIME_OFF_HOURS_PER_REQUEST
      );
      const maxDurationMs = maxHours * 60 * 60 * 1000;

      if (toDateTime < fromDateTime) {
        setTimeOffError('End date/time cannot be earlier than start date/time');
      } else if (remainingCap <= 0) {
        setTimeOffError('Monthly time-off allowance of 6 hours has been utilized for this month.');
      } else if (diffMs > maxDurationMs + 1000) {
        setTimeOffError(
          maxHours < MAX_TIME_OFF_HOURS_PER_REQUEST
            ? `Time off duration cannot exceed your remaining monthly allowance (${formatHoursLabel(maxHours)} hours).`
            : 'Time off duration cannot be more than 6 hours.'
        );
      } else {
        setTimeOffError('');
      }
    } else {
      setTimeOffError('');
    }
  }, [dateTimeforTimeOff, selectedCategory, timeOffMonthlyUsage]);

   useEffect(() => {
    if (selectedCategory === 'Monthly Pay Slip' && newLeave.payslip_from_date && newLeave.payslip_to_date) {
      const fromDate = new Date(newLeave.payslip_from_date);
      const toDate = new Date(newLeave.payslip_to_date);

      if (toDate < fromDate) {
        setDateError('To date cannot be earlier than from date');
      } else {
        setDateError('');
      }
    } else if (selectedCategory === 'Monthly Pay Slip') {
      setDateError('');
    }
  }, [newLeave.payslip_from_date, newLeave.payslip_to_date, selectedCategory]);

  const handleApproveLeave = async (leaveId, action, comments) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/hr-requests/${leaveId}/approve`,
        { action, comments: comments || '' },
        { headers: { ...headers, 'Content-Type': 'application/json' } }
      );
      setSelectedLeave(null);
      await fetchHRequests();
      const statusLabel = response.data?.status || (action === 'approve' ? 'approved' : 'rejected');
      setPopupMessage({
        success: `Request updated to "${statusLabel}".`,
        failure: null,
        info: null,
      });
    } catch (error) {
      console.error('Error processing leave request:', error);
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => d.msg || JSON.stringify(d)).join('\n')
          : 'Error processing leave request. Please try again.';
      setPopupMessage({ success: null, failure: message, info: null });
    }
  };

  const buildDeleteConfirmMessage = (leave) => {
    const parts = ['Delete this request? This cannot be undone.'];
    if (leave?.tracking_id) {
      parts.push(`Request ID: ${leave.tracking_id}`);
    }
    if (leave?.employee_name) {
      parts.push(`Employee: ${formatEmployeeNameWithNo(leave.employee_name, leave.employee_no)}`);
    }
    return parts.join('\n');
  };

  const handleDeleteRequest = async (leave) => {
    const requestId = leave?.id || leave?.hr_request_id;
    if (!requestId) return;
    if (!window.confirm(buildDeleteConfirmMessage(leave))) return;

    try {
      const headers = await getAuthHeaders();
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/hr-requests/${requestId}`,
        { headers }
      );
      setSelectedLeave(null);
      await fetchHRequests();
      setPopupMessage({
        success: 'Request deleted successfully.',
        failure: null,
        info: null,
      });
    } catch (error) {
      console.error('Error deleting HR request:', error);
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => d.msg || JSON.stringify(d)).join('\n')
          : 'Error deleting request. Please try again.';
      setPopupMessage({ success: null, failure: message, info: null });
    }
  };

  const canDeleteRequests = canDeleteHrRequest(userProfile);
  const isDeletedView = currentView === 'deleted_requests';

  const handleCardDeleteClick = (event, leave) => {
    event.stopPropagation();
    handleDeleteRequest(leave);
  };

  const getPageTitle = () => {
    if (currentView === 'deleted_requests') {
      return 'Deleted Requests';
    }
    if (currentView === 'my_admin_requests') {
      return 'My Admin Requests';
    }
    if (currentView === 'admin_requests') {
      const isAdminSuper =
        userProfile?.role === 'admin' || userProfile?.role === 'super';
      return isAdminSuper ? 'All Admin Requests' : 'Admin Requests';
    }
    if (currentView === 'my_leave_requests') {
      return 'My Leave Requests';
    }
    switch (userProfile?.role) {
      case 'admin':
      case 'super':
        return 'All Leave Requests';
      case 'manager':
        return currentView === 'my_team_requests' ? 'Team Requests' : 'My Requests';
      case 'staff':
        return 'My Requests';
      default:
        return 'All Leave Requests';
    }
  };

  const requestFormRoles = ['staff', 'manager', 'admin', 'super'];
  const showRequestHeaderActions =
    !isDeletedView &&
    currentView !== 'my_team_requests' &&
    requestFormRoles.includes(userProfile?.role);
  const showMobileRequestFab =
    !isDeletedView &&
    (currentView === 'requests' ||
      currentView === 'admin_requests' ||
      currentView === 'my_leave_requests' ||
      currentView === 'my_admin_requests') &&
    requestFormRoles.includes(userProfile?.role);

  const CalendarIcon = () => (
    <svg width="1.5em" height="1.5em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
  );

  const sectionOptions = ['BOH', 'FOH'];
  const resumeStatusOptions = ['Late', 'Early', 'On Time'];

  const categoryFilterOptions = categories
    .map((category) => {
      const value = category.value ?? category.label ?? category.name;
      if (!value) return null;
      return { label: category.label ?? value, value };
    })
    .filter(Boolean);

  const leaveTypeFilterOptions = leaveTypes
    .map((type) => {
      const value = type.value ?? type.label ?? type.name;
      if (!value) return null;
      return { label: type.label ?? value, value };
    })
    .filter(Boolean);

  const getLeaveTypeColor = (type) => {
    switch (type) {
      case 'Annual Leave':
        return 'text-blue-600';
      case 'Sick Leave':
        return 'text-purple-600';
      case 'Emergency Leave':
        return 'text-red-600';
      case 'Maternity Leave':
        return 'text-pink-600';
      case 'Paternity Leave':
        return 'text-indigo-600';
      case 'Unpaid Leave':
        return 'text-gray-500';
      default:
        return 'text-gray-700';
    }
  };

  const formatSubmittedAt = (leave) => {
    const v = leave?.applied_date;
    if (v == null || v === '') return '—';
    if (currentView === 'my_team_requests') {
      return formatUsDateTimeFromIso(v) || formatUsDateFromDateTime(v) || formatUsDateFromIso(v) || '—';
    }
    return formatUsDateFromDateTime(v) || formatUsDateFromIso(v) || '—';
  };

  const formatDeletedAt = (leave) => {
    const v = leave?.deleted_at;
    if (v == null || v === '') return '—';
    return formatUsDateTimeFromIso(v) || formatUsDateFromDateTime(v) || formatUsDateFromIso(v) || '—';
  };

  const formatExpiresAt = (leave) => {
    const v = leave?.expires_at;
    if (v == null || v === '') return '—';
    return formatUsDateTimeFromIso(v) || formatUsDateFromDateTime(v) || formatUsDateFromIso(v) || '—';
  };

  const showLeaveBalanceOnPage =
    !isDeletedView &&
    (currentView === 'my_leave_requests' || currentView === 'requests');
  const displayLeaveBalance = leaveBalance ?? 0;
  const daysRequested = Number(newLeave.days_requested) || 0;
  const projectedRemaining = displayLeaveBalance - daysRequested;
  const showLeaveBalancePreview =
    isLeaveRequestCategory(selectedCategory) &&
    newLeave.start_date &&
    newLeave.end_date &&
    newLeave.end_date >= newLeave.start_date;

  const thisTimeOffRequestHours = computeTimeOffDurationHours(
    dateTimeforTimeOff.time_from_date,
    dateTimeforTimeOff.time_from_hour,
    dateTimeforTimeOff.time_to_date,
    dateTimeforTimeOff.time_to_hour
  );
  const showTimeOffBalancePreview = isTimeOffRequestCategory(selectedCategory);

  return (
    <div className="leave-management relative">
      <ReusablePopup
        success={popupMessage.success}
        failure={popupMessage.failure}
        info={popupMessage.info}
        onClose={() => {
          setPopupMessage({ success: null, failure: null, info: null });
          setFormSubmitError('');
        }}
      />
      <DetailedLeavePopup
        leave={selectedLeave}
        onClose={() => setSelectedLeave(null)}
        userProfile={userProfile}
        currentView={currentView}
        isDeletedView={isDeletedView}
        handleApproveLeave={handleApproveLeave}
        handleDeleteRequest={handleDeleteRequest}
        canDelete={canDeleteRequests && !isDeletedView}
        getLeaveTypeColor={getLeaveTypeColor}
      />
      <div className="section-header bg-[#674936] text-white p-4 rounded">
        <div className="flex flex-col gap-1 min-w-0 flex-1">
          <h3 className='flex gap-4 text-white text-[1rem]  md:text-[1.6rem] font-bold'><CalendarIcon /> {getPageTitle()}</h3>
          {showLeaveBalanceOnPage && (
            <p
              className={`text-sm md:text-base font-semibold pl-[calc(1.5em+1rem)] ${
                displayLeaveBalance < 0 ? 'text-red-200' : 'text-white/90'
              }`}
            >
              Leave balance: {displayLeaveBalance} day{displayLeaveBalance === 1 ? '' : 's'}
            </p>
          )}
        </div>
        {showRequestHeaderActions && (
          <div className="flex gap-3">
            <button 
              onClick={() => window.open('https://teams.microsoft.com/l/chat/19:meeting_YzA1NmE3YmItZTczYi00ODFkLTkzODEtY2FiMTI5NmIwYTkx@thread.v2/conversations?context=%7B%22contextType%22%3A%22chat%22%7D', '_blank')}
              className="hidden md:block bg-white text-[#674936] border-none px-6 py-3 rounded-lg cursor-pointer text-sm transition-colors duration-300 font-semibold font-['Titillium_Web'] hover:bg-[#e2dbd6]"
            >
              Contact Us
            </button>
            <button onClick={() => setShowRequestForm(true)} className="hidden md:block bg-white text-[#674936] border-none px-6 py-3 rounded-lg cursor-pointer text-sm transition-colors duration-300 font-semibold font-['Titillium_Web'] hover:bg-[#e2dbd6]">
              Request
            </button>
          </div>
        )}
      </div>
      {showMobileRequestFab && (
        <div className="fixed bottom-12 right-14 z-50 md:hidden bg-[#674936] p-3 shadow-lg rounded-full text-white cursor-pointer transition hover:bg-[#5c371f]" onClick={() => setShowRequestForm(true)}>
          <FaCirclePlus className='text-2xl' />
        </div>
      )}
      <div className="my-4 flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-4">
          {(userProfile?.role !== 'staff' || isDeletedView) && (
            <>
              <div className="w-full sm:max-w-xs">
                <label htmlFor="hr-request-id-filter" className="block text-sm font-medium text-gray-700 mb-1">
                  Request ID
                </label>
                <input
                  id="hr-request-id-filter"
                  type="text"
                  value={requestIdFilter}
                  onChange={(e) => setRequestIdFilter(e.target.value)}
                  placeholder="Search by Request ID"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#674936]"
                />
              </div>
              <div className="w-full sm:max-w-xs">
                <label htmlFor="hr-employee-id-filter" className="block text-sm font-medium text-gray-700 mb-1">
                  Emp ID
                </label>
                <input
                  id="hr-employee-id-filter"
                  type="text"
                  value={employeeNoFilter}
                  onChange={(e) => setEmployeeNoFilter(e.target.value)}
                  placeholder="Search by Emp ID"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#674936]"
                />
              </div>
            </>
          )}
          {!isDeletedView && (
            <>
              <div className="w-full sm:max-w-xs">
                <UsDatePicker
                  id="hr-applied-from-filter"
                  label="Start Date"
                  value={appliedFromDraft}
                  onChange={(iso) => {
                    setAppliedFromDraft(iso);
                    if (iso && appliedToDraft && appliedToDraft < iso) {
                      setAppliedDateError('End date must be on or after the start date.');
                    } else {
                      setAppliedDateError('');
                    }
                  }}
                  labelClassName="block text-sm font-medium text-gray-700 mb-1"
                  inputClassName="w-full border border-gray-300 rounded-md px-3 py-2 pr-12 text-sm focus:outline-none focus:border-[#674936]"
                />
              </div>
              <div className="w-full sm:max-w-xs">
                <UsDatePicker
                  id="hr-applied-to-filter"
                  label="End Date"
                  value={appliedToDraft}
                  onChange={(iso) => {
                    setAppliedToDraft(iso);
                    if (appliedFromDraft && iso && iso < appliedFromDraft) {
                      setAppliedDateError('End date must be on or after the start date.');
                    } else {
                      setAppliedDateError('');
                    }
                  }}
                  min={appliedFromDraft || undefined}
                  onBelowMin={() =>
                    setAppliedDateError('End date must be on or after the start date.')
                  }
                  labelClassName="block text-sm font-medium text-gray-700 mb-1"
                  inputClassName="w-full border border-gray-300 rounded-md px-3 py-2 pr-12 text-sm focus:outline-none focus:border-[#674936]"
                />
              </div>
              <div className="flex items-end gap-2 pb-0.5">
                <button
                  type="button"
                  onClick={handleApplyDateFilter}
                  disabled={!canApplyDateFilter || loading}
                  className="bg-[#674936] text-white px-4 py-2 rounded-lg hover:bg-[#503220] transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Go
                </button>
                <button
                  type="button"
                  onClick={handleClearDateFilter}
                  disabled={loading || (!appliedFromDraft && !appliedToDraft && !appliedFromQuery && !appliedToQuery)}
                  className="bg-white text-[#674936] border border-[#674936] px-4 py-2 rounded-lg hover:bg-[#ede9e7] transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Clear
                </button>
              </div>
              {appliedDateError ? (
                <p className="text-red-500 text-sm w-full pb-2">{appliedDateError}</p>
              ) : null}
              {fetchError ? (
                <p className="text-red-500 text-sm w-full pb-2">{fetchError}</p>
              ) : null}
              <div className="pb-0.5">
                <button
                  type="button"
                  onClick={handleExport}
                  disabled={
                    exporting
                    || loading
                    || isAppliedDateRangeInvalid(appliedFromQuery, appliedToQuery)
                  }
                  className="bg-[#674936] text-white px-4 py-2 rounded-lg hover:bg-[#503220] transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {exporting ? 'Exporting…' : 'Export to Excel'}
                </button>
              </div>
              {exportError ? (
                <p className="text-red-500 text-sm w-full pb-2">{exportError}</p>
              ) : null}
            </>
          )}
        </div>
      </div>

      {showRequestForm && (
        <div className="modal">
          <div className="modal-content !p-4">
            <h3 className='w-full flex justify-center items-center'>Request Leave</h3>
            <form onSubmit={handleSubmitLeave} noValidate>
              <div className="form-field">
                <label>Select Category *</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map((category, index) => (
                    <option key={index} value={category.value ?? category.label ?? category.name}>
                      {category?.value ?? category?.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Only show leave type fields when "Leave Request" category is selected */}
              {isLeaveRequestCategory(selectedCategory) && (
                <>
                  <div className="form-field">
                    <label>Leave Type *</label>
                    <select
                      value={newLeave.leave_type_id}
                      onChange={(e) => setNewLeave({ ...newLeave, leave_type_id: e.target.value })}
                      required
                    >
                      <option value="">Select Leave Type</option>
                      {leaveTypes.map((type, index) => (
                        <option key={index} value={type.id}>
                          {type?.value}
                        </option>
                      ))}

                    </select>
                  </div>

                  <div className="form-row mt-2">
                    <div className="form-field">
                      <UsDatePicker
                        ref={leaveStartDateRef}
                        id="leave-start-date"
                        label="Start Date *"
                        value={newLeave.start_date}
                        onChange={(iso) =>
                          setNewLeave((prev) => ({ ...prev, start_date: iso }))
                        }
                        min={allowPastLeaveDates ? undefined : todayIsoLocal()}
                        required
                        onBelowMin={() =>
                          setDateError(
                            allowPastLeaveDates ? '' : 'Start date must be today or later.'
                          )
                        }
                        hint="Enter as MM/DD/YYYY or use the calendar."
                      />
                    </div>
                    <div className="form-field">
                      <UsDatePicker
                        ref={leaveEndDateRef}
                        id="leave-end-date"
                        label="End Date *"
                        value={newLeave.end_date}
                        onChange={(iso) =>
                          setNewLeave((prev) => ({ ...prev, end_date: iso }))
                        }
                        min={
                          allowPastLeaveDates
                            ? (newLeave.start_date || undefined)
                            : (!newLeave.start_date || newLeave.start_date < todayIsoLocal()
                                ? todayIsoLocal()
                                : newLeave.start_date)
                        }
                        required
                        onBelowMin={() =>
                          setDateError(
                            allowPastLeaveDates
                              ? 'End date must be on or after the start date.'
                              : 'End date must be on or after the start date and not before today.'
                          )
                        }
                        hint="Enter as MM/DD/YYYY or use the calendar."
                      />
                      {dateError && <p className="text-red-500 text-sm">{dateError}</p>}
                    </div>
                  </div>

                  {showLeaveBalancePreview && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm space-y-1">
                      <p className="text-gray-700">
                        <span className="font-semibold">Current balance:</span>{' '}
                        {displayLeaveBalance} day{displayLeaveBalance === 1 ? '' : 's'}
                      </p>
                      <p className="text-gray-700">
                        <span className="font-semibold">Days requested:</span> {daysRequested}
                      </p>
                      <p className={projectedRemaining < 0 ? 'text-red-600 font-semibold' : 'text-gray-700'}>
                        <span className="font-semibold">Remaining balance:</span>{' '}
                        {projectedRemaining} day{projectedRemaining === 1 || projectedRemaining === -1 ? '' : 's'}
                      </p>
                    </div>
                  )}

                  <div className="form-field">
                    <label>PH Status</label>
                    <select
                      value={newLeave.ph_status ? 'yes' : 'no'}
                      onChange={(e) => setNewLeave({ ...newLeave, ph_status: e.target.value === 'yes' })}
                    >
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>

                  <div className="form-field">
                    <label>Advance Salary</label>
                    <select
                      value={newLeave.advance_salary ? 'yes' : 'no'}
                      onChange={(e) => setNewLeave({ ...newLeave, advance_salary: e.target.value === 'yes' })}
                    >
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                </>
              )}
              {isTimeOffRequestCategory(selectedCategory) && (
                <>
                <div className="form-row mt-2">
                  <div className="form-field">
                    <label>From Date *</label>
                    <input
                      type="date"
                      name="time_from_date"
                      value={dateTimeforTimeOff.time_from_date}
                      onChange={handleTimeOffChange}
                      className="w-full border rounded focus:outline-none focus:border-gray-500"
                      min={todayIsoLocal()}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label>From Time *</label>
                    <input
                      type="time"
                      name="time_from_hour"
                      value={dateTimeforTimeOff.time_from_hour}
                      onChange={handleTimeOffChange}
                      className="w-full border rounded focus:outline-none focus:border-gray-500"
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label>To Date *</label>
                    <input
                      type="date"
                      name="time_to_date"
                      value={dateTimeforTimeOff.time_to_date}
                      className="w-full border rounded focus:outline-none focus:border-gray-500"
                      min={dateTimeforTimeOff.time_from_date || todayIsoLocal()}
                      disabled
                      readOnly
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label>To Time *</label>
                    <input
                      type="time"
                      name="time_to_hour"
                      value={dateTimeforTimeOff.time_to_hour}
                      onChange={handleTimeOffChange}
                      className="w-full border rounded focus:outline-none focus:border-gray-500"
                      min={dateTimeforTimeOff.time_from_hour || undefined}
                      max={maxTimeOffEndTime || undefined}
                      required
                    />
                  </div>
                  {timeOffError && <p className="text-red-500 text-sm col-span-2">{timeOffError}</p>}
                </div>
                {showTimeOffBalancePreview && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm space-y-1 mt-2">
                    <p className="text-gray-700">
                      <span className="font-semibold">Monthly allowance:</span>{' '}
                      {formatHoursLabel(timeOffMonthlyUsage?.allowance_hours ?? MAX_TIME_OFF_HOURS_PER_REQUEST)} hours
                    </p>
                    <p className="text-gray-700">
                      <span className="font-semibold">Used this month:</span>{' '}
                      {formatHoursLabel(timeOffMonthlyUsage?.used_hours ?? 0)} hours
                    </p>
                    <p
                      className={
                        (timeOffMonthlyUsage?.remaining_hours ?? MAX_TIME_OFF_HOURS_PER_REQUEST) <= 0
                          ? 'text-red-600 font-semibold'
                          : 'text-gray-700'
                      }
                    >
                      <span className="font-semibold">Remaining:</span>{' '}
                      {formatHoursLabel(
                        timeOffMonthlyUsage?.remaining_hours ?? MAX_TIME_OFF_HOURS_PER_REQUEST
                      )}{' '}
                      hours
                    </p>
                    {(dateTimeforTimeOff.time_from_hour && dateTimeforTimeOff.time_to_hour) && (
                      <p className="text-gray-700">
                        <span className="font-semibold">This request:</span>{' '}
                        {formatHoursLabel(thisTimeOffRequestHours)} hours
                      </p>
                    )}
                  </div>
                )}
                </>
              )}
              {selectedCategory === 'Monthly Pay Slip' && (
                <div className="form-row mt-2">
                  <div className="form-field">
                    <label>From Date *</label>
                    <input
                      type="date"
                      value={newLeave.payslip_from_date}
                      onChange={(e) => setNewLeave({ ...newLeave, payslip_from_date: e.target.value })}
                      className="w-full border rounded focus:outline-none focus:border-gray-500"
                      min={todayIsoLocal()}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label>To Date *</label>
                    <input
                      type="date"
                      value={newLeave.payslip_to_date}
                      onChange={(e) => setNewLeave({ ...newLeave, payslip_to_date: e.target.value })}
                      className="w-full border rounded focus:outline-none focus:border-gray-500"
                      min={newLeave.payslip_from_date || todayIsoLocal()}
                      required
                    />
                  </div>
                  {dateError && <p className="text-red-500 text-sm col-span-2">{dateError}</p>}
                </div>
              )}
              {selectedCategory === 'Location Transfer Request' && (
                <div className="form-row mt-2">
                  <div className="form-field">
                    <label>Reallocation Reason *</label>
                    <input
                      type="text"
                      value={newLeave.reallocation_reason}
                      onChange={(e) => setNewLeave({ ...newLeave, reallocation_reason: e.target.value })}
                      className="w-full border rounded focus:outline-none focus:border-gray-500"
                      placeholder="e.g., Family emergency"
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label>Transfer From Location *</label>
                    <select
                      value={newLeave.transfer_from_location}
                      onChange={(e) => setNewLeave({ ...newLeave, transfer_from_location: e.target.value })}
                      className="w-full border rounded focus:outline-none focus:border-gray-500"
                      required
                    >
                      <option value="">Select Location</option>
                      {locations.map((location, index) => (
                        <option key={index} value={location}>
                          {location}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Transfer To Location *</label>
                    <select
                      value={newLeave.transfer_to_location}
                      onChange={(e) => setNewLeave({ ...newLeave, transfer_to_location: e.target.value })}
                      className="w-full border rounded focus:outline-none focus:border-gray-500"
                      required
                    >
                      <option value="">Select Location</option>
                      {locations.map((location, index) => (
                        <option key={index} value={location}>
                          {location}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Effective From Date *</label>
                    <input
                      type="date"
                      value={newLeave.effective_from_date}
                      onChange={(e) => setNewLeave({ ...newLeave, effective_from_date: e.target.value })}
                      className="w-full border rounded focus:outline-none focus:border-gray-500"
                      min={todayIsoLocal()}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label>Section *</label>
                    <select
                      value={newLeave.section}
                      onChange={(e) => setNewLeave({ ...newLeave, section: e.target.value })}
                      className="w-full border rounded focus:outline-none focus:border-gray-500"
                      required
                    >
                      <option value="">Select Section</option>
                      {sectionOptions.map((section, index) => (
                        <option key={index} value={section}>
                          {section}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              {selectedCategory === 'Leave Resume' && (
                <div className="form-row mt-2">
                  <div className="form-field">
                    <label>Resume Status *</label>
                    <select
                      value={newLeave.resume_status}
                      onChange={(e) => setNewLeave({ ...newLeave, resume_status: e.target.value })}
                      className="w-full border rounded focus:outline-none focus:border-gray-500"
                      required
                    >
                      <option value="">Select Resume Status</option>
                      {resumeStatusOptions.map((status, index) => (
                        <option key={index} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Actual Resume Date *</label>
                    <input
                      type="date"
                      value={newLeave.actual_resume_date}
                      onChange={(e) => setNewLeave({ ...newLeave, actual_resume_date: e.target.value })}
                      className="w-full border rounded focus:outline-none focus:border-gray-500"
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label>Resume Remarks</label>
                    <input
                      type="text"
                      value={newLeave.resume_remarks}
                      onChange={(e) => setNewLeave({ ...newLeave, resume_remarks: e.target.value })}
                      className="w-full border rounded focus:outline-none focus:border-gray-500"
                      placeholder="e.g., Got into an accident"
                    />
                  </div>
                  <div className="form-field">
                    <label>Leave Request Reference *</label>
                    <select
                      value={newLeave.leave_request_reference}
                      onChange={(e) => setNewLeave({ ...newLeave, leave_request_reference: e.target.value })}
                      className="w-full border rounded focus:outline-none focus:border-gray-500"
                      required
                    >
                      <option value="">Select Reference</option>
                      {leaveRequestReferences.map((ref, index) => (
                        <option key={index} value={ref.tracking_id}>
                          {ref.tracking_id}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              {isReasonFieldVisible(selectedCategory, onlyReason) &&
                <div className="form-field mt-2">
                  <label>Reason *</label>
                  <textarea
                    placeholder="Enter reason for request"
                    value={newLeave.reason}
                    onChange={(e) => setNewLeave({ ...newLeave, reason: e.target.value })}
                    required
                  />
                </div>
              }
              {selectedCategory && (
                <div className="form-field mt-4">
                  <label>
                    Attachments
                    {attachmentsRequiredForLeave ? (
                      <span className="text-red-600"> *</span>
                    ) : null}
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      {attachmentsRequiredForLeave
                        ? `(required for this leave type — PDF, Office, images; max ${MAX_ATTACHMENTS} files, ${MAX_FILE_BYTES / (1024 * 1024)} MB each)`
                        : `(optional — PDF, Office, images; max ${MAX_ATTACHMENTS} files, ${MAX_FILE_BYTES / (1024 * 1024)} MB each)`}
                    </span>
                  </label>
                  <input
                    type="file"
                    multiple
                    accept={ATTACHMENT_ACCEPT}
                    onChange={handleAddHrRequestFiles}
                    disabled={hrRequestFiles.length >= MAX_ATTACHMENTS}
                    className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#674936] file:text-white hover:file:bg-[#503220] disabled:opacity-60"
                  />
                  {attachmentsRequiredForLeave && hrRequestFiles.length === 0 && (
                    <p className="mt-1 text-sm text-red-600">
                      At least one attachment is required for this leave type.
                    </p>
                  )}
                  {hrRequestFiles.length > 0 && (
                    <ul className="mt-2 space-y-2">
                      {hrRequestFiles.map((file, idx) => (
                        <li
                          key={`${file.name}-${idx}`}
                          className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm"
                        >
                          <div className="min-w-0 mr-2">
                            <p className="text-gray-800 font-medium truncate">{file.name}</p>
                            <p className="text-xs text-gray-500">{formatAttachmentFileSize(file.size)}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveHrRequestFile(idx)}
                            className="text-red-600 hover:text-red-800 text-sm font-semibold shrink-0"
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {formSubmitError ? (
                <div
                  role="alert"
                  className="mt-4 p-3 rounded-lg border border-red-200 bg-red-50 text-red-800 text-sm whitespace-pre-line"
                >
                  {formSubmitError}
                </div>
              ) : null}
              <div className="form-actions">
                <button
                  type="button"
                  className={`submit-button disabled:opacity-50 disabled:cursor-not-allowed ${isSubmitting ? 'opacity-50 !cursor-not-allowed' : ''}`}
                  disabled={isSubmitting}
                  onClick={handleSubmitLeave}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                </button>
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={closeRequestForm}
                  className={`cancel-button ${isSubmitting ? 'opacity-30 !cursor-not-allowed' : ''}`}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div
        className={`relative overflow-x-auto border border-gray-200 rounded-lg w-full shadow-sm mx-auto ${
          openHeaderFilter ? 'min-h-[24rem]' : ''
        }`}
      >
        {loading && leaves.length > 0 && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
            <Loader />
          </div>
        )}
        <table className="min-w-full divide-y divide-gray-200 text-sm text-left">
          <thead className="bg-[#674936]">
            <tr>
              <th className="px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider whitespace-nowrap">
                Request ID
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider whitespace-nowrap">
                Employee
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider whitespace-nowrap">
                Emp ID
              </th>
              <th className="px-4 py-3 relative whitespace-nowrap">
                <ColumnCategoryFilterHeader
                  label="Category"
                  variant="dark"
                  options={categoryFilterOptions}
                  selectedValues={categoryFilter}
                  onChange={setCategoryFilter}
                  searchable
                  searchPlaceholder="Search category..."
                  onOpenChange={(isOpen) => handleHeaderFilterOpenChange('category', isOpen)}
                />
              </th>
              <th className="px-4 py-3 relative whitespace-nowrap">
                <ColumnCategoryFilterHeader
                  label="Leave Type"
                  variant="dark"
                  options={leaveTypeFilterOptions}
                  selectedValues={leaveTypeFilter}
                  onChange={setLeaveTypeFilter}
                  searchable
                  searchPlaceholder="Search leave type..."
                  onOpenChange={(isOpen) => handleHeaderFilterOpenChange('leaveType', isOpen)}
                />
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider whitespace-nowrap">
                <div className="flex items-center gap-1">
                  Submitted
                  {!isDeletedView && (
                    <span className="mt-0.5 flex flex-col">
                      <MdKeyboardArrowUp
                        className={`-mb-1 text-base cursor-pointer ${
                          sortOrder === 'asc' ? 'opacity-40 cursor-default' : 'hover:opacity-80'
                        }`}
                        onClick={() => handleSubmittedSort('asc')}
                        aria-label="Sort submitted date oldest first"
                      />
                      <MdKeyboardArrowDown
                        className={`text-base cursor-pointer ${
                          sortOrder === 'desc' ? 'opacity-40 cursor-default' : 'hover:opacity-80'
                        }`}
                        onClick={() => handleSubmittedSort('desc')}
                        aria-label="Sort submitted date newest first"
                      />
                    </span>
                  )}
                </div>
              </th>
              {isDeletedView && (
                <>
                  <th className="px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider whitespace-nowrap">
                    Deleted
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider whitespace-nowrap">
                    Expires
                  </th>
                </>
              )}
              <th className="px-4 py-3 relative whitespace-nowrap">
                <ColumnCategoryFilterHeader
                  label="Status"
                  variant="dark"
                  options={HR_STATUS_FILTER_OPTIONS}
                  selectedValues={statusFilter}
                  onChange={setStatusFilter}
                  onOpenChange={(isOpen) => handleHeaderFilterOpenChange('status', isOpen)}
                />
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider whitespace-nowrap">
                Attachments
              </th>
              {canDeleteRequests && !isDeletedView && (
                <th className="px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider whitespace-nowrap">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {leaves.map((leave, key) => (
              <tr
                key={leave.id || leave.tracking_id || key}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => setSelectedLeave(leave)}
              >
                <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                  {leave.tracking_id || '—'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                  {leave.employee_name || '—'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                  {leave.employee_no || '—'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                  {leave.category_name || 'Unknown'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                  {leave.leave_type_name || '—'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                  {formatSubmittedAt(leave)}
                </td>
                {isDeletedView && (
                  <>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                      {formatDeletedAt(leave)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                      {formatExpiresAt(leave)}
                    </td>
                  </>
                )}
                <td className="px-4 py-3 whitespace-nowrap">
                  <span
                    className={`status text-sm font-medium px-3 py-1 rounded-full 
                      ${leave?.status === 'Request Submitted' ? 'bg-gray-100 text-gray-800' : ''}
                      ${leave?.status === 'Manager Approved' ? 'bg-green-100 text-green-800' : ''}
                      ${leave?.status === 'Payroll Manager Approved' ? 'bg-green-100 text-green-800' : ''}
                      ${leave?.status === 'HR Manager Approved' ? 'bg-green-100 text-green-800' : ''}
                      ${leave?.status === 'Manager Rejected' ? 'bg-red-100 text-red-800' : ''}
                      ${leave?.status === 'Payroll Manager Rejected' ? 'bg-red-100 text-red-800' : ''}
                      ${leave?.status === 'HR Manager Rejected' ? 'bg-red-100 text-red-800' : ''}
                    `}
                  >
                    {leave.status}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <RequestAttachmentBadge leave={leave} />
                </td>
                {canDeleteRequests && !isDeletedView && (
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button
                      type="button"
                      onClick={(event) => handleCardDeleteClick(event, leave)}
                      className="bg-[#fed7d7] text-[#e53e3e] px-3 py-1 rounded-lg hover:bg-[#ffb3b3] transition-colors duration-300 font-semibold text-xs"
                    >
                      Delete
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(totalCount > 0 || leaves.length > 0) && (
        <div className="flex flex-wrap justify-center items-center gap-4 mt-8">
          <div className="flex items-center gap-2">
            <label htmlFor="hr-requests-page-size" className="text-sm text-gray-700 whitespace-nowrap">
              Rows per page
            </label>
            <select
              id="hr-requests-page-size"
              value={limit}
              onChange={handlePageSizeChange}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#674936] bg-white"
            >
              {HR_REQUESTS_PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
          {totalCount > limit && (
            <>
              <button
                onClick={() => skip > 0 && setSkip(skip - limit)}
                disabled={skip === 0}
                className={`px-4 py-2 rounded ${skip === 0 ? 'bg-gray-300 cursor-not-allowed' : 'bg-[#674936] text-white hover:bg-[#503220]'}`}
              >
                Previous
              </button>
              <span>
                Page {Math.floor(skip / limit) + 1} of {Math.ceil(totalCount / limit)}
              </span>
              <button
                onClick={() => skip + limit < totalCount && setSkip(skip + limit)}
                disabled={skip + limit >= totalCount}
                className={`px-4 py-2 rounded ${skip + limit >= totalCount ? 'bg-gray-300 cursor-not-allowed' : 'bg-[#674936] text-white hover:bg-[#503220]'}`}
              >
                Next
              </button>
            </>
          )}
        </div>
      )}
      {leaves.length === 0 && !loading && (
        <div className="no-data">
          <p>No leave requests found.</p>
        </div>
      )}
      {leaves.length === 0 && loading && (
        <Loader />
      )}
    </div>
  );
};

export default RequestManagement;